import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { MealPlansService } from '@/services/meal-plans';
import { 
  MealPlanWithItems,
  CreateMealPlanRequest,
  AddMealPlanItemRequest,
  UpdateMealPlanItemRequest
} from '@/types/api';

// Query keys for React Query
export const mealPlanKeys = {
  all: ['meal-plans'] as const,
  lists: () => [...mealPlanKeys.all, 'list'] as const,
  list: (page: number, limit: number) => [...mealPlanKeys.lists(), page, limit] as const,
  detail: (id: string) => [...mealPlanKeys.all, 'detail', id] as const,
  current: () => [...mealPlanKeys.all, 'current'] as const,
  week: (weekStart: string) => [...mealPlanKeys.all, 'week', weekStart] as const,
  stats: (id: string) => [...mealPlanKeys.all, 'stats', id] as const,
  shoppingList: (id: string) => [...mealPlanKeys.all, 'shopping-list', id] as const,
};

/**
 * Hook for getting meal plans (paginated)
 */
export function useMealPlans(page: number = 1, limit: number = 10) {
  return useQuery({
    queryKey: mealPlanKeys.list(page, limit),
    queryFn: () => MealPlansService.getMealPlans(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for getting a specific meal plan by ID
 */
export function useMealPlan(id: string, options?: Omit<UseQueryOptions<MealPlanWithItems, Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: mealPlanKeys.detail(id),
    queryFn: () => MealPlansService.getMealPlanById(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute (shorter since meal plans change frequently)
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook for getting current week's meal plan
 */
export function useCurrentWeekMealPlan() {
  return useQuery({
    queryKey: mealPlanKeys.current(),
    queryFn: () => MealPlansService.getCurrentWeekMealPlan(),
    staleTime: 30 * 1000, // 30 seconds (very fresh for current week)
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for getting meal plan by specific week
 */
export function useMealPlanByWeek(weekStart: string) {
  return useQuery({
    queryKey: mealPlanKeys.week(weekStart),
    queryFn: () => MealPlansService.getMealPlanByWeek(weekStart),
    enabled: !!weekStart,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for getting meal plan statistics
 */
export function useMealPlanStats(id: string) {
  return useQuery({
    queryKey: mealPlanKeys.stats(id),
    queryFn: () => MealPlansService.getMealPlanStats(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for getting shopping list
 */
export function useShoppingList(mealPlanId: string) {
  return useQuery({
    queryKey: mealPlanKeys.shoppingList(mealPlanId),
    queryFn: () => MealPlansService.generateShoppingList(mealPlanId),
    enabled: !!mealPlanId,
    staleTime: 30 * 1000, // 30 seconds (fresh for shopping)
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating meal plan
 */
export function useCreateMealPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMealPlanRequest) => MealPlansService.createMealPlan(data),
    onSuccess: (newPlan) => {
      // Invalidate meal plans list
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.lists() });
      // Invalidate current week if it matches
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
      // Set the new meal plan in cache
      queryClient.setQueryData(mealPlanKeys.detail(newPlan.id), newPlan);
    },
  });
}

/**
 * Hook for updating meal plan
 */
export function useUpdateMealPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateMealPlanRequest> }) =>
      MealPlansService.updateMealPlan(id, data),
    onSuccess: (updatedPlan, variables) => {
      // Update specific meal plan cache
      queryClient.setQueryData(mealPlanKeys.detail(variables.id), updatedPlan);
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
    },
  });
}

/**
 * Hook for deleting meal plan
 */
export function useDeleteMealPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => MealPlansService.deleteMealPlan(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: mealPlanKeys.detail(deletedId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
    },
  });
}

/**
 * Hook for adding item to meal plan
 */
export function useAddMealPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mealPlanId, data }: { mealPlanId: string; data: AddMealPlanItemRequest }) =>
      MealPlansService.addMealPlanItem(mealPlanId, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific meal plan to refresh items
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.detail(variables.mealPlanId) });
      // Invalidate stats and shopping list
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.stats(variables.mealPlanId) });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.shoppingList(variables.mealPlanId) });
      // Invalidate current week if it matches
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
    },
  });
}

/**
 * Hook for updating meal plan item
 */
export function useUpdateMealPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateMealPlanItemRequest }) =>
      MealPlansService.updateMealPlanItem(itemId, data),
    onSuccess: (updatedItem) => {
      // Invalidate the meal plan that contains this item
      queryClient.invalidateQueries({ 
        queryKey: mealPlanKeys.detail(updatedItem.mealPlanId) 
      });
      // Invalidate shopping list and stats
      queryClient.invalidateQueries({ 
        queryKey: mealPlanKeys.shoppingList(updatedItem.mealPlanId) 
      });
      queryClient.invalidateQueries({ 
        queryKey: mealPlanKeys.stats(updatedItem.mealPlanId) 
      });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
    },
  });
}

/**
 * Hook for removing meal plan item
 */
export function useRemoveMealPlanItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => MealPlansService.removeMealPlanItem(itemId),
    onSuccess: () => {
      // Invalidate all meal plan related queries since we don't know which plan it belonged to
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.all });
    },
  });
}
